/**
 * Vision UI-inspired dashboard primitives for EntrepreNatives.
 *
 * Goal: brighten our existing violet/indigo dashboard with glassmorphic
 * cards, gradient icon tiles, animated count-up stats, mini sparklines and
 * a decorative aurora backdrop — all using our existing #8B5CF6 / #0E0E23
 * brand palette plus three accent ramps (violet, fuchsia, cyan).
 *
 * No new deps. Uses framer-motion + recharts already in the project.
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  Area, AreaChart, ResponsiveContainer, Tooltip,
} from 'recharts';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';

/* ─────────────────────────────────────────────────────────────
 *  Color ramps
 * ─────────────────────────────────────────────────────────── */
export const RAMPS = {
  violet:  { from: '#8B5CF6', to: '#6D28D9', glow: 'rgba(139,92,246,0.45)' },
  fuchsia: { from: '#EC4899', to: '#A21CAF', glow: 'rgba(236,72,153,0.45)' },
  cyan:    { from: '#22D3EE', to: '#0EA5E9', glow: 'rgba(34,211,238,0.45)' },
  amber:   { from: '#F59E0B', to: '#D97706', glow: 'rgba(245,158,11,0.45)' },
  lime:    { from: '#A3E635', to: '#65A30D', glow: 'rgba(163,230,53,0.45)' },
};

export const POSITIVE = '#A3E635';
export const NEGATIVE = '#FB7185';

/* ─────────────────────────────────────────────────────────────
 *  GlassCard — semi-transparent glass tile with inner border
 * ─────────────────────────────────────────────────────────── */
export const GlassCard = ({ children, className = '', glow = false, ...props }) => (
  <div
    className={`relative rounded-2xl border border-white/[0.08] bg-gradient-to-br from-white/[0.05] to-white/[0.015] backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.35)] transition-all duration-300 ${glow ? 'hover:border-[#8B5CF6]/40 hover:shadow-[0_8px_38px_rgba(139,92,246,0.25)]' : ''} ${className}`}
    {...props}
  >
    {children}
  </div>
);

/* ─────────────────────────────────────────────────────────────
 *  GradientIconTile — Vision UI's signature gradient pill icon
 * ─────────────────────────────────────────────────────────── */
export const GradientIconTile = ({ icon: Icon, ramp = 'violet', size = 'md', className = '' }) => {
  const r = RAMPS[ramp] || RAMPS.violet;
  const dims = size === 'sm' ? 'w-9 h-9' : size === 'lg' ? 'w-14 h-14' : 'w-11 h-11';
  const iconSize = size === 'sm' ? 16 : size === 'lg' ? 24 : 20;
  return (
    <div
      className={`${dims} rounded-xl flex items-center justify-center shrink-0 ${className}`}
      style={{
        background: `linear-gradient(135deg, ${r.from}, ${r.to})`,
        boxShadow: `0 8px 24px -6px ${r.glow}`,
      }}
    >
      <Icon size={iconSize} className="text-white" strokeWidth={2.2} />
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
 *  CountUp — animate a number from 0 → target (rAF, no deps)
 * ─────────────────────────────────────────────────────────── */
export const CountUp = ({
  value = 0,
  duration = 1100,
  prefix = '',
  suffix = '',
  decimals = 0,
  format = null,
  className = '',
  'data-testid': testid,
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const from = 0;
    const to = Number(value) || 0;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setDisplay(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);

  const out = format
    ? format(display)
    : `${prefix}${display.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}${suffix}`;

  return <span ref={ref} className={className} data-testid={testid}>{out}</span>;
};

/* ─────────────────────────────────────────────────────────────
 *  MiniSparkline — 56px-tall recharts area, gradient fill
 * ─────────────────────────────────────────────────────────── */
const SPARK_GRAD_ID = (id) => `spark-grad-${id}`;
export const MiniSparkline = ({ data = [], ramp = 'violet', id = 'a', height = 56 }) => {
  const r = RAMPS[ramp] || RAMPS.violet;
  const series = data.length
    ? data
    : [4, 9, 6, 12, 8, 15, 13, 18].map((v, i) => ({ x: i, y: v }));
  return (
    <div style={{ height }} className="w-full -mx-1 -mb-1">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series} margin={{ top: 6, right: 4, left: 4, bottom: 0 }}>
          <defs>
            <linearGradient id={SPARK_GRAD_ID(id)} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={r.from} stopOpacity={0.55} />
              <stop offset="100%" stopColor={r.from} stopOpacity={0}    />
            </linearGradient>
          </defs>
          <Tooltip cursor={false} contentStyle={{ display: 'none' }} />
          <Area
            type="monotone"
            dataKey="y"
            stroke={r.from}
            strokeWidth={2}
            fill={`url(#${SPARK_GRAD_ID(id)})`}
            isAnimationActive
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
 *  StatCardV2 — glass + gradient icon + count-up + sparkline
 * ─────────────────────────────────────────────────────────── */
export const StatCardV2 = ({
  icon,
  label,
  value,
  prefix = '',
  suffix = '',
  decimals = 0,
  format = null,
  change,
  positive = true,
  ramp = 'violet',
  spark = [],
  sparkId,
  'data-testid': testid,
  delay = 0,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1], delay }}
      data-testid={testid}
    >
      <GlassCard glow className="p-5 pb-3 h-full">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.16em] text-[#A5A8C9] font-semibold mb-1.5">
              {label}
            </p>
            <CountUp
              value={value}
              prefix={prefix}
              suffix={suffix}
              decimals={decimals}
              format={format}
              className="block text-2xl sm:text-3xl font-bold text-white font-syne tracking-tight"
              data-testid={testid ? `${testid}-value` : undefined}
            />
          </div>
          <GradientIconTile icon={icon} ramp={ramp} />
        </div>

        {change !== undefined && change !== null && (
          <div className="flex items-center gap-1.5 text-xs">
            <span
              className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full font-bold"
              style={{
                color: positive ? POSITIVE : NEGATIVE,
                background: positive ? 'rgba(163,230,53,0.10)' : 'rgba(251,113,133,0.10)',
                border: `1px solid ${positive ? 'rgba(163,230,53,0.25)' : 'rgba(251,113,133,0.25)'}`,
              }}
            >
              {positive ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
              {Math.abs(change)}%
            </span>
            <span className="text-[#7B7FA8] text-[11px]">vs last period</span>
          </div>
        )}

        <MiniSparkline data={spark} ramp={ramp} id={sparkId || testid || label} />
      </GlassCard>
    </motion.div>
  );
};

/* ─────────────────────────────────────────────────────────────
 *  DashboardBackdrop — page-level decorative aurora + dotted globe
 * ─────────────────────────────────────────────────────────── */
export const DashboardBackdrop = ({ globe = true }) => (
  <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-none" aria-hidden="true">
    {/* Page gradient wash — kept subtle so existing layout background still shows through if any */}
    <div
      className="absolute inset-0"
      style={{
        background:
          'radial-gradient(1200px 600px at 85% -10%, rgba(139,92,246,0.22), transparent 60%),' +
          'radial-gradient(900px 500px at -10% 20%, rgba(236,72,153,0.13), transparent 60%),' +
          'radial-gradient(800px 600px at 50% 100%, rgba(34,211,238,0.08), transparent 60%)',
      }}
    />
    {/* Dotted globe artwork (Vision-UI homage) */}
    {globe && (
      <svg
        className="absolute -right-24 -top-16 w-[760px] h-[760px] opacity-[0.18] mix-blend-screen"
        viewBox="0 0 800 800"
        fill="none"
      >
        <defs>
          <radialGradient id="globe-grad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#A78BFA" />
            <stop offset="100%" stopColor="#312E81" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="400" cy="400" r="320" fill="url(#globe-grad)" opacity="0.4" />
        {/* Latitude ellipses */}
        {[0.3, 0.55, 0.8, 1.0, 0.8, 0.55, 0.3].map((scaleY, i) => (
          <ellipse
            key={`lat-${i}`}
            cx="400"
            cy={150 + i * 83}
            rx={320 * (1 - Math.abs(0.5 - i / 6) * 0.4)}
            ry="6"
            stroke="#C4B5FD"
            strokeWidth="0.6"
            strokeDasharray="2 4"
            fill="none"
          />
        ))}
        {/* Longitude ellipses */}
        {[0.2, 0.55, 0.85, 1.0, 0.85, 0.55, 0.2].map((rx, i) => (
          <ellipse
            key={`lng-${i}`}
            cx="400"
            cy="400"
            rx={320 * rx}
            ry="320"
            stroke="#C4B5FD"
            strokeWidth="0.6"
            strokeDasharray="2 4"
            fill="none"
            transform={`rotate(${i * 25 - 75} 400 400)`}
          />
        ))}
      </svg>
    )}
  </div>
);

/* ─────────────────────────────────────────────────────────────
 *  GradientAreaChart — main "Vision UI" chart for revenue
 * ─────────────────────────────────────────────────────────── */
export const GradientAreaChart = ({
  data,
  series = [{ key: 'revenue', label: 'Revenue', ramp: 'violet' }],
  height = 280,
  yFormatter = (v) => `$${v}`,
}) => {
  const series2 = series.map((s) => ({ ...s, _ramp: RAMPS[s.ramp] || RAMPS.violet }));
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
          <defs>
            {series2.map((s, i) => (
              <linearGradient key={i} id={`area-grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={s._ramp.from} stopOpacity={0.55} />
                <stop offset="100%" stopColor={s._ramp.from} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              return (
                <div className="rounded-xl border border-white/10 bg-[#13153A]/95 backdrop-blur px-4 py-3 shadow-2xl">
                  <p className="text-[11px] uppercase tracking-wider text-[#9CA3AF] mb-1">{label}</p>
                  {payload.map((p, i) => (
                    <p key={i} className="text-sm font-bold" style={{ color: p.color }}>
                      {p.name}: {yFormatter(p.value)}
                    </p>
                  ))}
                </div>
              );
            }}
          />
          {series2.map((s, i) => (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={s._ramp.from}
              strokeWidth={2.5}
              fill={`url(#area-grad-${i})`}
              dot={false}
              activeDot={{ r: 5, fill: s._ramp.from, stroke: '#0E0E23', strokeWidth: 2 }}
              isAnimationActive
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
